<script>
    import {onMount} from 'svelte';
    import {slide} from 'svelte/transition';
    import {cubicInOut} from 'svelte/easing';

    import {userStore} from '@js/stores/userStore';
    import {mapStore} from '@js/stores/mapStore';

    import * as Utils from '@js/modules/utils';

    import {MAP_CLASS, MAP_MODULES} from '@js/modules/maps/mapConstants';
    import MapBuilder from '@js/modules/maps/MapBuilder';
    import MapLayers from '@js/components/maps/MapLayers.svelte';
    import MapElevationGraph from '@js/components/maps/MapElevationGraph.svelte';
    import MapElevationButton from '@js/components/maps/MapElevationButton.svelte';

    import AnalyticsService from '@js/modules/analyticsService';

    const {
        mapId,
        rideId,
        rideName,
        rideShareUrl,
        ridePoisCount,
        initialLocation
    } = $props();

    let _previousUnit = userStore.unit;

    let isMapLoaded = $state(false);
    let isShowElevation = $state(false);

    const _displayElevationOnMap = (altitudeElement) => {
        if (!altitudeElement || !mapStore.map) {
            return;
        }

        const altitudeValue = altitudeElement.getAttribute('data-altitude');
        const altitudeLocation = JSON.parse(altitudeElement.getAttribute('data-location'));

        Utils.addMultipleEventListener(altitudeElement, 'mouseover touchend', () => {
            if (!altitudeLocation || !mapStore.map) {
                return;
            }

            mapStore.map.showViewPointAtPoint({
                lat: altitudeLocation[0],
                lng: altitudeLocation[1]
            }, altitudeValue);
        });

        Utils.addMultipleEventListener(altitudeElement, 'mouseout', () => {
            if (!mapStore.map) {
                return;
            }

            mapStore.map.removeViewPointMarker();
        });
    };

    const _handleMapStyleChange = (mapStyle) => {
        if (!mapStore.map) {
            return;
        }

        mapStore.map.setMapStyle(mapStyle);
    };

    const _handleHighlightMapElement = (element) => {
        if (!mapStore.map) {
            return;
        }

        mapStore.map.highlightMapElement(element);
    };

    const _handleElevationClick = () => {
        if (window.isMetricsEnabled && !isShowElevation) {
            AnalyticsService.trackShowElevationClick(rideName, rideId);
        }

        isShowElevation = !isShowElevation;
    };

    const _loadRideMap = () => {
        MapBuilder.createMap(
            MAP_CLASS.RIDE,
            [
                MAP_MODULES.GRAPH,
                MAP_MODULES.RIDE_POIS
            ],
            {
                mapId,
                rideId,
                rideShareUrl,
                ridePoisCount,
                initialLocation,
                defaultZoom: 6,
                gestureEnabled: window.innerWidth < window.settings.medium_screen
            }
        )
            .then((map) => {
                if (!map) {
                    return;
                }

                mapStore.map = map;

                isMapLoaded = true;

                const minAltElement = document.getElementById(`ride-min-elevation-${rideId}`);
                _displayElevationOnMap(minAltElement);
                const maxAltElement = document.getElementById(`ride-max-elevation-${rideId}`);
                _displayElevationOnMap(maxAltElement);

                if (initialLocation) {
                    mapStore.startPointLocation = {
                        lat: initialLocation[0],
                        lng: initialLocation[1]
                    };
                }

                userStore.subscribe((userState) => {
                    if (userState.unit !== _previousUnit) {
                        _previousUnit = userState.unit;

                        mapStore.map.onUnitChanged(userState.unit);
                    }
                });
            });
    };

    onMount(() => {
        if (!mapStore.map) {
            _loadRideMap();
        }

        return () => {
            if (mapStore.map) {
                mapStore.map.destroy();
                mapStore.map = null;
            }
        };
    });
</script>

<div id={mapId}
     class="relative">
    <div class="map-area">
        <div id={`${mapId}-map`}
             class="map-canvas map-read-only relative">
            {#if $userStore.featureFlags.map_available_layers?.length}
                <MapLayers mapLayersAvailable={$userStore.featureFlags.map_available_layers}
                           onMapStyleChange={_handleMapStyleChange}
                           onHighlightMapElement={_handleHighlightMapElement}/>
            {/if}
        </div>
    </div>

    <div class="ride-map-elevation">
        <MapElevationButton hasElevationData={true}
                            onElevationToggle={_handleElevationClick}/>
    </div>
</div>

{#if isShowElevation && isMapLoaded}
    <div transition:slide={{duration: 300, easing: cubicInOut}}>
        <MapElevationGraph map={$mapStore.map}
                           rideId={rideId}
                           rideShareUrl={rideShareUrl}
                           warningHelper={true}/>
    </div>
{/if}
